import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BusFront, Route } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard } from '../themed';
import OperationalUnavailabilityPanel from './OperationalUnavailabilityPanel';

const OperationalUnavailabilityWorkspace = ({ entityId, adminMode = true }) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  const requestGenerationRef = useRef(0);
  const translationRef = useRef(t);
  const [scope, setScope] = useState('route');
  const [routes, setRoutes] = useState([]);
  const [routeCatalogComplete, setRouteCatalogComplete] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;

    // Entity changes revoke every previously selected target immediately.
    // A late response must not repopulate another entity's management view.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- A tenant switch must revoke stale scoped options before issuing the replacement request.
    setRoutes([]);
    setRouteCatalogComplete(false);
    setVehicles([]);
    setSelectedRouteId('');
    setSelectedVehicleId('');

    if (!entityId) {
      setLoading(false);
      return undefined;
    }

    const includeInactiveRoutes = Boolean(adminMode);
    setLoading(true);
    Promise.all([
      axios.get(`/api/routes/${entityId}`, {
        params: { includeInactive: includeInactiveRoutes },
      }),
      adminMode
        ? axios.get(`/api/vehicles/${entityId}`)
        : axios.get(`/api/operational-unavailability/${entityId}/vehicle-targets`),
    ])
      .then(([routeResponse, vehicleResponse]) => {
        if (requestGenerationRef.current !== requestGeneration) return;
        // These established endpoints return bare arrays, unlike the newer
        // operational-unavailability response envelope.
        const routeData = Array.isArray(routeResponse.data) ? routeResponse.data : [];
        setRoutes(routeData);
        // Completeness is a fact about the accepted unfiltered response, not
        // merely the caller role or the resulting array length.
        setRouteCatalogComplete(includeInactiveRoutes && Array.isArray(routeResponse.data));
        const vehicleData = adminMode
          ? vehicleResponse.data
          : vehicleResponse.data?.data;
        setVehicles(Array.isArray(vehicleData) ? vehicleData : []);
      })
      .catch(() => {
        if (requestGenerationRef.current === requestGeneration) {
          setRouteCatalogComplete(false);
          toast.error(translationRef.current('operations.targetsLoadFailed'));
        }
      })
      .finally(() => {
        if (requestGenerationRef.current === requestGeneration) {
          setLoading(false);
        }
      });

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [adminMode, entityId]);

  const selectedRoute = routes.find(
    (route) => Number(route.id) === Number(selectedRouteId),
  );
  const routeStops = (selectedRoute?.waypoints || []).filter((waypoint) => (
    waypoint.point_type === 'stop'
    && waypoint.status !== 'inactive'
    && Number(waypoint.active ?? 1) === 1
  ));

  const selectedTargetId = scope === 'route'
    ? selectedRouteId
    : selectedVehicleId;
  const targets = useMemo(
    () => (scope === 'route' ? routes : vehicles),
    [routes, scope, vehicles],
  );

  const selectTarget = (value) => {
    if (scope === 'route') {
      setSelectedRouteId(value);
    } else {
      setSelectedVehicleId(value);
    }
  };

  return (
    <div className="w-full pb-8">
      <ThemedCard>
        <ThemedCard.Header>
          <div>
            <h2 className={cn('text-xl font-semibold', text.primary)}>
              {t('operations.serviceUnavailability')}
            </h2>
            <p className={cn('mt-1 text-sm', text.muted)}>
              {t(adminMode
                ? 'operations.adminUnavailabilityHelp'
                : 'operations.driverUnavailabilityHelp')}
            </p>
          </div>
        </ThemedCard.Header>
        <ThemedCard.Body>
          <div className="grid gap-4 md:grid-cols-2">
            <fieldset>
              <legend className={cn('mb-2 text-sm font-medium', text.secondary)}>
                {t('operations.resourceType')}
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'route', icon: Route, label: t('operations.route') },
                  { value: 'vehicle', icon: BusFront, label: t('operations.shuttle') },
                ].map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={scope === value}
                    className={cn(
                      'inline-flex min-h-11 items-center justify-center gap-2 rounded border px-3 py-2',
                      border.default,
                      scope === value
                        ? 'bg-amber-600 text-white'
                        : `${background.primary} ${text.secondary}`,
                    )}
                    onClick={() => setScope(value)}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className={cn('block text-sm font-medium', text.secondary)}>
              {scope === 'route'
                ? t('operations.selectRoute')
                : t('operations.selectShuttle')}
              <select
                value={selectedTargetId}
                onChange={(event) => selectTarget(event.target.value)}
                disabled={loading || targets.length === 0}
                className={cn(
                  'mt-2 min-h-11 w-full rounded border px-3 py-2',
                  background.primary,
                  border.default,
                  text.primary,
                )}
              >
                <option value="">
                  {loading
                    ? t('loading')
                    : (scope === 'route'
                      ? t('operations.chooseRoute')
                      : t('operations.chooseShuttle'))}
                </option>
                {targets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {scope === 'route' ? target.name : target.vehicle_number}
                    {target.status === 'inactive'
                      ? ` (${t('operations.inactive')})`
                      : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!loading && targets.length === 0 && (
            <div className={cn(
              'mt-4 flex items-start gap-2 rounded border p-3 text-sm',
              border.default,
              text.muted,
            )}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {scope === 'route'
                  ? t('operations.noRoutes')
                  : t('operations.noShuttles')}
              </span>
            </div>
          )}

          <OperationalUnavailabilityPanel
            entityId={entityId}
            targetType={scope}
            targetId={selectedTargetId || null}
            aggregateMode
            routes={routes}
            vehicles={vehicles}
            routeStops={scope === 'route' ? routeStops : []}
            routeCatalogComplete={routeCatalogComplete}
          />
        </ThemedCard.Body>
      </ThemedCard>
    </div>
  );
};

export default OperationalUnavailabilityWorkspace;
